import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/shared/DashboardLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { analyticsApi } from '@/lib/api';
import useAppStore from '@/stores/appStore';
import { ArrowClockwise, FileText, CaretUp, CaretDown, MagnifyingGlass, Bell } from '@phosphor-icons/react';

// Modular Subcomponents
import { BrandingSettingsForm } from '@/components/admin/BrandingSettingsForm';
import { SlaSettingsForm } from '@/components/admin/SlaSettingsForm';
import { SmtpSettingsForm } from '@/components/admin/SmtpSettingsForm';
import { R2SettingsForm } from '@/components/admin/R2SettingsForm';

const getDiff = (before, after) => {
  const diff = {};
  const beforeObj = before || {};
  const afterObj = after || {};
  
  const keys = Array.from(new Set([...Object.keys(beforeObj), ...Object.keys(afterObj)]));
  
  for (const key of keys) {
    const beforeVal = beforeObj[key];
    const afterVal = afterObj[key];
    
    if (JSON.stringify(beforeVal) !== JSON.stringify(afterVal)) {
      diff[key] = {
        before: beforeVal,
        after: afterVal
      };
    }
  }
  return diff;
};

export const AdminConfig = () => {
  const tenantConfig = useAppStore((state) => state.tenantConfig);
  const setTenantConfig = useAppStore((state) => state.setTenantConfig);

  const [activeTab, setActiveTab] = useState('general');

  // Audit Logs State
  const [auditLogs, setAuditLogs] = useState([]);
  const [loadingAudit, setLoadingAudit] = useState(false);
  const [auditSearch, setAuditSearch] = useState('');
  const [auditAction, setAuditAction] = useState('all');
  const [auditEntity, setAuditEntity] = useState('all');
  const [auditPage, setAuditPage] = useState(1);
  const [auditTotalPages, setAuditTotalPages] = useState(1);
  const [expandedLogId, setExpandedLogId] = useState(null);

  const brandColorAccent = tenantConfig?.brand_colour || '#002FA7';

  const fetchAuditLogs = async (page = 1) => {
    setLoadingAudit(true);
    try {
      const params = {
        page,
        limit: 15,
      };
      if (auditSearch.trim()) params.search = auditSearch;
      if (auditAction !== 'all') params.action = auditAction;
      if (auditEntity !== 'all') params.entity_type = auditEntity;
      
      const { data } = await analyticsApi.getAuditLogs(params);
      setAuditLogs(data.items || []);
      setAuditTotalPages(data.pages || 1);
      setAuditPage(data.page || 1);
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
      toast.error('Failed to load audit logs');
    } finally {
      setLoadingAudit(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'audit') {
      fetchAuditLogs(auditPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, auditAction, auditEntity, auditPage]);


  return (
    <DashboardLayout role="super_admin">
      <div className="p-6 lg:p-8 max-w-4xl mx-auto space-y-8 animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between animate-fade-in-up">
          <div className="space-y-1">
            <h1 className="text-4xl font-black tracking-tight text-slate-900">Configuration</h1>
            <p className="text-muted-foreground text-sm">Manage organization settings and system operations</p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full space-y-6">
          <TabsList className="bg-slate-100 p-1 border border-slate-200 w-full md:w-auto grid grid-cols-2 md:grid-cols-5 md:max-w-3xl rounded-md h-auto gap-1">
            <TabsTrigger value="general" className="font-bold text-xs uppercase tracking-wider px-5 py-2.5 rounded-sm">
              General Settings
            </TabsTrigger>
            <TabsTrigger value="sla" className="font-bold text-xs uppercase tracking-wider px-5 py-2.5 rounded-sm">
              SLA & Shift Settings
            </TabsTrigger>
            <TabsTrigger value="notification" className="font-bold text-xs uppercase tracking-wider px-5 py-2.5 rounded-sm">
              Notification
            </TabsTrigger>
            <TabsTrigger value="audit" className="font-bold text-xs uppercase tracking-wider px-5 py-2.5 rounded-sm">
              Audit Logs
            </TabsTrigger>
            <TabsTrigger value="backups" className="font-bold text-xs uppercase tracking-wider px-5 py-2.5 rounded-sm">
              Backups
            </TabsTrigger>
          </TabsList>

          {/* GENERAL SETTINGS TAB */}
          <TabsContent value="general" className="space-y-8 mt-0 outline-none">
            <BrandingSettingsForm tenantConfig={tenantConfig} setTenantConfig={setTenantConfig} />
          </TabsContent>

          {/* SLA AND SHIFT SETTINGS TAB */}
          <TabsContent value="sla" className="space-y-8 mt-0 outline-none">
            <SlaSettingsForm tenantConfig={tenantConfig} setTenantConfig={setTenantConfig} />
          </TabsContent>

          {/* NOTIFICATION CHANNELS TAB */}
          <TabsContent value="notification" className="space-y-6 mt-0 outline-none">
            <Card className="p-6 border shadow-sm rounded-lg">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-100">
                <div className="p-2 bg-primary/10 rounded-sm text-primary">
                  <Bell size={24} style={{ color: brandColorAccent }} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-855 tracking-tight">Email Notification Settings</h2>
                  <p className="text-sm text-slate-500 mt-0.5">Configure SMTP email gateway for system notifications</p>
                </div>
              </div>
              <SmtpSettingsForm tenantConfig={tenantConfig} setTenantConfig={setTenantConfig} />
            </Card>
          </TabsContent>

          {/* AUDIT LOGS TAB */}
          <TabsContent value="audit" className="space-y-6 mt-0 outline-none">
            <Card className="p-6 border shadow-sm rounded-lg">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-100">
                <div className="p-2 bg-primary/10 rounded-sm text-primary">
                  <FileText size={24} style={{ color: brandColorAccent }} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-850">System Audit Logs</h2>
                  <p className="text-sm text-slate-500">Track and review critical data mutations, actions, and security logs</p>
                </div>
              </div>

              {/* Filters */}
              <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-6">
                <div className="md:col-span-6 relative">
                  <MagnifyingGlass 
                    size={20} 
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" 
                  />
                  <Input
                    placeholder="Search by actor, action, or entity ID..."
                    value={auditSearch}
                    onChange={(e) => setAuditSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        setAuditPage(1);
                        fetchAuditLogs(1);
                      }
                    }}
                    className="pl-10 h-10 border-slate-300"
                  />
                </div>
                
                <div className="md:col-span-3">
                  <Select value={auditAction} onValueChange={(val) => { setAuditAction(val); setAuditPage(1); }}>
                    <SelectTrigger className="h-10 border-slate-300 bg-white">
                      <SelectValue placeholder="Action" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Actions</SelectItem>
                      <SelectItem value="create">Create</SelectItem>
                      <SelectItem value="update">Update</SelectItem>
                      <SelectItem value="delete">Delete</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="md:col-span-3">
                  <Select value={auditEntity} onValueChange={(val) => { setAuditEntity(val); setAuditPage(1); }}>
                    <SelectTrigger className="h-10 border-slate-300 bg-white">
                      <SelectValue placeholder="Entity Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Entities</SelectItem>
                      <SelectItem value="users">Users</SelectItem>
                      <SelectItem value="products">Products</SelectItem>
                      <SelectItem value="product_models">Models</SelectItem>
                      <SelectItem value="product_categories">Categories</SelectItem>
                      <SelectItem value="tickets">Tickets</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Logs Table */}
              {loadingAudit ? (
                <div className="py-12 flex justify-center items-center">
                  <ArrowClockwise className="animate-spin text-slate-400" size={32} />
                </div>
              ) : auditLogs.length > 0 ? (
                <div className="space-y-4">
                  <div className="border rounded-lg overflow-hidden bg-white">
                    <div className="overflow-x-auto">
                      <table className="w-full text-left border-collapse text-xs">
                        <thead>
                          <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 font-bold uppercase tracking-wider">
                            <th className="py-3 px-4 w-[180px]">Timestamp</th>
                            <th className="py-3 px-4">Actor</th>
                            <th className="py-3 px-4">Action</th>
                            <th className="py-3 px-4">Entity Type</th>
                            <th className="py-3 px-4">Entity ID</th>
                            <th className="py-3 px-4 text-right">Details</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 font-medium text-slate-700">
                          {auditLogs.map((log) => {
                            const isExpanded = expandedLogId === log.id;
                            const hasDiff = log.before_data || log.after_data;
                            
                            return (
                              <>
                                <tr 
                                  key={log.id} 
                                  className={`hover:bg-slate-50/50 transition-colors cursor-pointer ${isExpanded ? 'bg-slate-50' : ''}`}
                                  onClick={() => hasDiff && setExpandedLogId(isExpanded ? null : log.id)}
                                >
                                  <td className="py-3 px-4 text-slate-500 font-mono">
                                    {new Date(log.created_at).toLocaleString()}
                                  </td>
                                  <td className="py-3 px-4">
                                    {log.user_name || log.actor_name ? (
                                      <div>
                                        <div className="font-bold text-slate-900">{log.user_name || log.actor_name}</div>
                                        <div className="text-[10px] text-slate-400">
                                          {log.user_email || log.actor_email || log.user_role || log.actor_phone}
                                        </div>
                                      </div>
                                    ) : (
                                      <span className="text-slate-400 italic font-semibold text-xs">System (Automated)</span>
                                    )}
                                  </td>
                                  <td className="py-3 px-4">
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold border ${
                                      log.action === 'create' 
                                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200' 
                                        : log.action === 'update' 
                                        ? 'bg-blue-50 text-blue-700 border-blue-200' 
                                        : 'bg-red-50 text-red-700 border-red-200'
                                    }`}>
                                      {log.action.toUpperCase()}
                                    </span>
                                  </td>
                                  <td className="py-3 px-4 font-mono text-slate-650">{log.entity_type}</td>
                                  <td className="py-3 px-4 font-mono text-slate-400 truncate max-w-[120px]" title={log.entity_id}>
                                    {log.entity_id || '—'}
                                  </td>
                                  <td className="py-3 px-4 text-right">
                                    {hasDiff ? (
                                      <button className="text-primary hover:underline font-bold text-xs inline-flex items-center gap-1">
                                        {isExpanded ? (
                                          <>Hide <CaretUp size={12} /></>
                                        ) : (
                                          <>Show <CaretDown size={12} /></>
                                        )}
                                      </button>
                                    ) : (
                                      <span className="text-slate-300">—</span>
                                    )}
                                  </td>
                                </tr>
                                
                                {isExpanded && hasDiff && (
                                  <tr className="bg-slate-50/50" key={`${log.id}-expanded`}>
                                    <td colSpan={6} className="py-4 px-6">
                                      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden shadow-sm">
                                        <table className="w-full text-left border-collapse">
                                          <thead>
                                            <tr className="bg-slate-50 border-b border-slate-200 text-[10px] font-black uppercase text-slate-400 tracking-wider">
                                              <th className="py-2.5 px-4">Field</th>
                                              <th className="py-2.5 px-4">Previous Value</th>
                                              <th className="py-2.5 px-4"></th>
                                              <th className="py-2.5 px-4">New Value</th>
                                            </tr>
                                          </thead>
                                          <tbody className="divide-y divide-slate-100 text-xs font-medium text-slate-700">
                                            {(() => {
                                              const diff = getDiff(log.before_data, log.after_data);
                                              const diffKeys = Object.keys(diff).filter(k => k !== 'updated_at');
                                              
                                              if (diffKeys.length === 0) {
                                                return (
                                                  <tr>
                                                    <td colSpan={4} className="py-4 px-4 text-center text-slate-400 italic">
                                                      No changes detected or only internal metadata updated
                                                    </td>
                                                  </tr>
                                                );
                                              }
                                              
                                              return diffKeys.map((key) => {
                                                const beforeVal = diff[key].before;
                                                const afterVal = diff[key].after;
                                                
                                                const formatValue = (val) => {
                                                  if (val === null || val === undefined) return <span className="text-slate-400 italic">null</span>;
                                                  if (typeof val === 'object') return JSON.stringify(val);
                                                  return String(val);
                                                };
                                                
                                                return (
                                                  <tr key={key} className="hover:bg-slate-50/50">
                                                    <td className="py-3 px-4 font-bold text-slate-800">{key}</td>
                                                    <td className="py-3 px-4 font-mono text-slate-600 break-all max-w-xs">
                                                      {log.action === 'create' ? (
                                                        <span className="text-slate-400 italic">—</span>
                                                      ) : (
                                                        formatValue(beforeVal)
                                                      )}
                                                    </td>
                                                    <td className="py-3 px-4 text-slate-400 text-center">➔</td>
                                                    <td className="py-3 px-4 font-mono text-slate-900 break-all max-w-xs">
                                                      {log.action === 'delete' ? (
                                                        <span className="text-slate-400 italic">—</span>
                                                      ) : (
                                                        formatValue(afterVal)
                                                      )}
                                                    </td>
                                                  </tr>
                                                );
                                              });
                                            })()}
                                          </tbody>
                                        </table>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Pagination */}
                  <div className="flex items-center justify-between pt-4">
                    <p className="text-xs text-slate-500 font-semibold">
                      Page <span className="text-slate-800">{auditPage}</span> of <span className="text-slate-800">{auditTotalPages}</span>
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fetchAuditLogs(auditPage - 1)}
                        disabled={auditPage === 1 || loadingAudit}
                        className="font-bold text-xs uppercase tracking-wider h-8"
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fetchAuditLogs(auditPage + 1)}
                        disabled={auditPage === auditTotalPages || loadingAudit}
                        className="font-bold text-xs uppercase tracking-wider h-8"
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="py-12 border border-dashed rounded-lg text-center space-y-2">
                  <FileText className="mx-auto text-slate-300" size={40} />
                  <h3 className="font-bold text-slate-700">No Audit Logs Found</h3>
                  <p className="text-xs text-slate-400">Data mutations and security actions will appear here automatically.</p>
                </div>
              )}
            </Card>
          </TabsContent>

          {/* BACKUPS TAB */}
          <TabsContent value="backups" className="space-y-6 mt-0 outline-none">
            <R2SettingsForm tenantConfig={tenantConfig} setTenantConfig={setTenantConfig} />
          </TabsContent>
        </Tabs>

      </div>
    </DashboardLayout>
  );
};
